import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { addErrorNotification } from "modules/notification/redux/notificationSlice";
import { selectCurrentEventId } from "modules/event/selectors";
import { useI18n } from "i18n";
import { selectCurrentSpaceId } from "modules/space/redux/selectors";
import { useAppDispatch } from "store/hooks";
import logger from "logging/logger";
import {
  JoinRoomStatus,
  selectCurrentRoomId,
  updateRoomStatus,
} from "store/rooms";
import {
  CustomAppEvent,
  CustomAppEvents,
} from "modules/audioVideo/DailyEvents";
import { useAppMessage } from "@daily-co/daily-react";
import { useFindParticipant } from "modules/audioVideo/hooks/useParticipant/findParticipant";
import hostSeatRequest, { HostSeatPayload } from "./hostSeatRequest";
import getErrorMessage from "./errorHandler";
import { HostSeatErrors } from "./constants";

export const useHostSeat = () => {
  const eventId = useSelector(selectCurrentEventId);
  const currentFloorId = useSelector(selectCurrentSpaceId);
  const currentTableId = useSelector(selectCurrentRoomId);
  const sendMessage = useAppMessage<CustomAppEvent>();

  const { findParticipant } = useFindParticipant();

  const { t } = useI18n(["server", "template"]);
  const dispatch = useAppDispatch();

  const {
    mutate: switchToHostSeat,
    isPending,
    status,
  } = useMutation({
    mutationFn: async ({ userId }: Pick<HostSeatPayload, "userId">) => {
      if (!currentFloorId || !eventId || !currentTableId) {
        throw new Error(HostSeatErrors.MISSING_DATA);
      }

      const payload = {
        floorId: currentFloorId,
        eventId,
        tableId: currentTableId,
        userId,
      };

      logger.info("host switching to host seat", payload);

      dispatch(updateRoomStatus(JoinRoomStatus.IN_PROGRESS));
      return hostSeatRequest(payload);
    },

    onSuccess: (_, { userId }) => {
      // we throw in the mutation if this is null.
      if (!currentTableId) {
        return;
      }

      const participantSessionId = findParticipant(userId);
      sendMessage(
        {
          event: CustomAppEvents.HOST_SEAT_REQUEST,
          tableId: currentTableId,
        },
        participantSessionId,
      );
    },
    onError: (error: Error) => {
      const errorMessage = getErrorMessage(error.message);
      dispatch(
        addErrorNotification({
          message: t(errorMessage),
        }),
      );
    },
    onSettled: () => {
      dispatch(updateRoomStatus(JoinRoomStatus.DONE));
    },
  });

  return { switchToHostSeat, isPending, status };
};
